<template>
  <div class="chat-input-box agentic-home-chat-input" ref="chatBox" @keydown.esc="showPlotPicker = false">
    <div v-if="selectedPlots.length" class="selected-plot-chips">
      <div v-for="plot in selectedPlots" :key="plot.ID" class="selected-plot-chip">
        <v-icon size="13" class="chip-icon">
          {{ plot.AccessLevel === "public" ? "mdi-earth" : "mdi-lock-outline" }}
        </v-icon>
        <span class="selected-plot-name" :title="plot.Name">{{ plot.Name }}</span>
        <button type="button" :aria-label="`Remove ${plot.Name}`" @click="removePlot(plot.ID)">
          <v-icon size="13">mdi-close</v-icon>
        </button>
      </div>
      <button type="button" class="clear-plots-btn" @click="selectedPlots = []">Clear all</button>
    </div>

    <div class="chat-input-content">
      <textarea
        v-model="message"
        class="chat-input-field"
        :placeholder="placeholderText"
        @keydown="handleKeyPress"
        @input="handleInput"
        ref="chatInput"
        rows="3"
      ></textarea>
    </div>
    <div class="chat-input-actions">
      <div class="plot-picker-anchor">
        <button
          class="select-plots-btn"
          :class="{ open: showPlotPicker }"
          type="button"
          aria-haspopup="listbox"
          :aria-expanded="showPlotPicker ? 'true' : 'false'"
          @click.stop="togglePlotPicker"
        >
          <v-icon size="15">mdi-chart-box-outline</v-icon>
          <span>Select plots</span>
          <span v-if="selectedPlots.length" class="selected-count">{{ selectedPlots.length }}</span>
          <v-icon size="15" class="select-plots-caret">mdi-chevron-down</v-icon>
        </button>

        <div v-if="showPlotPicker" class="plot-picker" role="listbox" @click.stop>
          <div class="plot-picker-header">
            <div class="plot-picker-title">
              <span>Add existing plots</span>
              <small>{{ selectedPlots.length }} of {{ maxSelectedPlots }} selected</small>
            </div>
            <button
              type="button"
              class="plot-picker-close"
              aria-label="Close plot picker"
              @click="showPlotPicker = false"
            >
              <v-icon size="17">mdi-close</v-icon>
            </button>
          </div>

          <div class="plot-search">
            <img :src="searchIcon" alt="" width="16" height="16" class="plot-search-icon" />
            <input v-model="plotSearch" type="search" placeholder="Search plots..." />
          </div>

          <div class="plot-tabs" role="tablist" aria-label="Plot visibility">
            <button
              type="button"
              role="tab"
              :aria-selected="activePlotTab === 'mine' ? 'true' : 'false'"
              :class="{ active: activePlotTab === 'mine' }"
              @click="activePlotTab = 'mine'"
            >
              My Plots<span class="tab-count">{{ myPlotsCount }}</span>
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="activePlotTab === 'public' ? 'true' : 'false'"
              :class="{ active: activePlotTab === 'public' }"
              @click="activePlotTab = 'public'"
            >
              Public<span class="tab-count">{{ publicPlotsCount }}</span>
            </button>
          </div>

          <div class="plot-picker-list">
            <div v-if="loadingPlots" class="plot-picker-state">
              <v-progress-circular indeterminate size="18" width="2"></v-progress-circular>
              <span>Loading plots...</span>
            </div>
            <template v-else>
              <button
                v-for="plot in filteredPlots"
                :key="plot.ID"
                type="button"
                role="option"
                :aria-selected="isPlotSelected(plot) ? 'true' : 'false'"
                class="plot-option"
                :class="{ selected: isPlotSelected(plot) }"
                @click="togglePlot(plot)"
              >
                <span class="plot-option-check">
                  <v-icon v-if="isPlotSelected(plot)" size="13">mdi-check</v-icon>
                </span>
                <span class="plot-option-name" :title="plot.Name">{{ plot.Name }}</span>
                <span
                  class="plot-access-badge"
                  :class="plot.AccessLevel === 'public' ? 'public' : 'private'"
                >
                  <v-icon size="11">
                    {{ plot.AccessLevel === "public" ? "mdi-earth" : "mdi-lock-outline" }}
                  </v-icon>
                  {{ plot.AccessLevel === "public" ? "Public" : "Private" }}
                </span>
              </button>
              <div v-if="filteredPlots.length === 0" class="plot-picker-state">
                <v-icon size="20">mdi-chart-box-outline</v-icon>
                <span>No plots found</span>
              </div>
            </template>
          </div>

          <div class="plot-picker-footer">
            <button type="button" class="footer-clear" :disabled="!selectedPlots.length" @click="selectedPlots = []">
              Clear
            </button>
            <button type="button" class="footer-done" @click="showPlotPicker = false">Done</button>
          </div>
        </div>
      </div>

      <button
        class="chat-action-btn send-btn"
        @click="handleSend"
        type="button"
        :disabled="loading || (!message.trim() && !selectedPlots.length)"
      >
        <v-progress-circular v-if="loading" indeterminate color="primary" size="20" width="2"></v-progress-circular>
        <img v-else :src="sendIcon" alt="Send" />
      </button>
    </div>
  </div>
</template>

<script>
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";

export default {
  name: "ChatInputBox",
  props: {
    placeholderText: {
      type: String,
      default: "Let's create a dashboard...",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      message: "",
      sendIcon: require("@/assets/Images/Dashboard/send-2.png"),
      searchIcon: require("@/assets/Svg/Icons/search.svg"),
      plots: [],
      selectedPlots: [],
      showPlotPicker: false,
      loadingPlots: false,
      plotsLoaded: false,
      plotSearch: "",
      activePlotTab: "mine",
      maxSelectedPlots: 6,
    };
  },
  computed: {
    currentUserEmail() {
      return this.$store.getters["tenantId/getUser"]?.username || "";
    },
    myPlots() {
      return this.plots.filter(
        (plot) =>
          plot.AccessLevel === "private" ||
          (plot.AccessLevel === "public" && plot.CreatedBy === this.currentUserEmail)
      );
    },
    publicPlots() {
      return this.plots.filter((plot) => plot.AccessLevel === "public");
    },
    myPlotsCount() {
      return this.myPlots.length;
    },
    publicPlotsCount() {
      return this.publicPlots.length;
    },
    filteredPlots() {
      const query = this.plotSearch.trim().toLowerCase();
      const tabPlots = this.activePlotTab === "public" ? this.publicPlots : this.myPlots;
      if (!query) return tabPlots;
      return tabPlots.filter((plot) => (plot.Name || "").toLowerCase().includes(query));
    },
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
    this.$nextTick(() => {
      if (this.$refs.chatInput) {
        this.$refs.chatInput.focus();
      }
    });
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    handleOutsideClick(event) {
      if (this.showPlotPicker && this.$refs.chatBox && !this.$refs.chatBox.contains(event.target)) {
        this.showPlotPicker = false;
      }
    },
    togglePlotPicker() {
      this.showPlotPicker = !this.showPlotPicker;
      if (this.showPlotPicker && !this.plotsLoaded) {
        this.fetchPlots();
      }
    },
    async fetchPlots() {
      this.loadingPlots = true;
      try {
        const response = await api.get("public/api/charts", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": getTenantPrefix() || "",
          },
        });
        if (
          response?.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          this.plots = response.data.data?.charts || [];
          this.plotsLoaded = true;
        } else {
          throw new Error(response?.data?.message || "Unable to load plots");
        }
      } catch (error) {
        console.error("Error fetching plots:", error);
        this.$store.commit("api/showSnackbar", {
          message: error.response?.data?.message || error.message || "Unable to load plots",
          color: "error",
          timeout: 5000,
        });
      } finally {
        this.loadingPlots = false;
      }
    },
    isPlotSelected(plot) {
      return this.selectedPlots.some((selected) => selected.ID === plot.ID);
    },
    togglePlot(plot) {
      if (this.isPlotSelected(plot)) {
        this.removePlot(plot.ID);
        return;
      }
      if (this.selectedPlots.length >= this.maxSelectedPlots) {
        this.$store.commit("api/showSnackbar", {
          message: `You can select up to ${this.maxSelectedPlots} plots`,
          color: "warning",
          timeout: 3000,
        });
        return;
      }
      this.selectedPlots.push(plot);
    },
    removePlot(plotId) {
      this.selectedPlots = this.selectedPlots.filter((plot) => plot.ID !== plotId);
    },
    buildMessagePayload() {
      const typedMessage = this.message.trim();
      const plotNames = this.selectedPlots
        .map((plot) => `"${String(plot.Name || "").replace(/"/g, '\\"')}"`)
        .join(", ");

      if (typedMessage && plotNames) {
        return `${typedMessage}\n\nUse the following existing plots: ${plotNames}.`;
      }
      if (plotNames) {
        return `Create a dashboard using the following existing plots: ${plotNames}.`;
      }
      return typedMessage;
    },
    handleSend() {
      const payload = this.buildMessagePayload();
      if (!payload) return;

      this.$emit("send-message", payload);
      this.message = "";
      this.selectedPlots = [];
      this.showPlotPicker = false;
      this.$nextTick(() => {
        if (this.$refs.chatInput) {
          this.$refs.chatInput.focus();
        }
      });
    },
    handleKeyPress(event) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.handleSend();
      }
    },
    handleInput(event) {
      this.message = event.target.value;
    },
  },
};
</script>

